import { RacemapColors } from '@racemap/utilities/consts/common';
import tinycolor from 'tinycolor2';

const MARKER_HEIGHT = 57;
const MARKER_WIDTH = 50;
const MARKER_PADDING = 0.5;
const MARKER_RADIUS = 4;
const TEXT_PADDING = 2;
const TRIANGLE_HEIGHT = 5;
const TRIANGLE_WIDTH = 10;
const ICON_PADDING_X = 8;
const ICON_PADDING_BOTTOM = 2;
const TEXT_HEIGHT = 20;
const measureCanvas = document.createElement('canvas');
const measureCanvasContext = measureCanvas.getContext('2d');

interface Dimensions {
  width: number;
  height: number;
  radius: number;
  textPadding: number;
  markerPadding: number;
  iconPaddingX: number;
  iconPaddingBottom: number;
  iconContainerHeight: number;
  iconContainerWidth: number;
  iconContainerStartX: number;
  iconContainerEndX: number;
  iconContainerStartY: number;
  iconContainerEndY: number;
  triangleHeight: number;
  triangleWidth: number;
  triangleStartX: number;
  triangleEndX: number;
  textStartX: number;
  textStartY: number;
  textEndX: number;
  textEndY: number;
}

function calculateDimensions({ resizeFactor = 1 }: { resizeFactor?: number }): Dimensions {
  if (!measureCanvasContext) throw new Error('Could not get 2d context');

  const height = MARKER_HEIGHT * resizeFactor;
  const width = MARKER_WIDTH * resizeFactor;
  const radius = MARKER_RADIUS * resizeFactor;
  const markerPadding = MARKER_PADDING * resizeFactor;
  const textPadding = TEXT_PADDING * resizeFactor;
  const iconPaddingX = ICON_PADDING_X * resizeFactor;
  const iconPaddingBottom = ICON_PADDING_BOTTOM * resizeFactor;
  const triangleHeight = TRIANGLE_HEIGHT * resizeFactor;
  const triangleWidth = TRIANGLE_WIDTH * resizeFactor;
  const textHeight = TEXT_HEIGHT * resizeFactor;

  const textStartX = markerPadding + textPadding;
  const textStartY = markerPadding + textPadding;
  const textEndX = width - markerPadding - textPadding;
  const textEndY = textStartY + textHeight;

  const iconContainerHeight =
    height - 2 * textPadding - 2 * markerPadding - triangleHeight - textHeight - iconPaddingBottom;
  const iconContainerWidth = width - 2 * textPadding - 2 * markerPadding - 2 * iconPaddingX;
  const iconContainerStartX = textStartX + iconPaddingX;
  const iconContainerEndX = textEndX - iconPaddingX;
  const iconContainerStartY = textEndY;
  const iconContainerEndY = iconContainerStartY + iconContainerHeight;

  const triangleStartX = width / 2 - triangleWidth / 2;
  const triangleEndX = width / 2 + triangleWidth / 2;

  return {
    width,
    height,
    radius,
    markerPadding,
    textPadding,
    iconPaddingX,
    iconPaddingBottom,
    triangleHeight,
    triangleWidth,
    iconContainerHeight,
    iconContainerWidth,
    iconContainerStartX,
    iconContainerEndX,
    iconContainerStartY,
    iconContainerEndY,
    triangleStartX,
    triangleEndX,
    textStartX,
    textStartY,
    textEndX,
    textEndY,
  };
}

export interface MarkerImageConfig {
  bgColor?: string;
  borderColor?: string;
  alpha?: number;
  size?: number; // Resize factor for the image
  lineWidth?: number; // Optional line width for the border
  svgPath?: string; // Optional SVG path for custom icons
  iconColor?: string; // Color for the icon, defaults to RacemapColors.PaleBlue
}

export const getEventMarkerImage = async ({
  bgColor = 'white',
  borderColor = RacemapColors.PaleBlue,
  alpha = 1,
  size = 1,
  svgPath = '',
  iconColor = RacemapColors.PaleBlue,
  lineWidth = 1, // Optional line width for the border
}: MarkerImageConfig): Promise<{ image: ImageBitmap; dimensions: Dimensions }> => {
  // Make the image not pixelized on devices with high DPR
  const canvas = document.createElement('canvas');
  const dpr = window.devicePixelRatio || 1;
  const resizeFactor = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get 2d context');

  const dimensions = calculateDimensions({ resizeFactor });
  const { width, height, radius, markerPadding, triangleHeight, triangleStartX, triangleEndX } =
    dimensions;
  canvas.width = width * dpr;
  canvas.height = height * dpr;

  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;

  ctx.scale(dpr, dpr);

  ctx.globalAlpha = 1;
  ctx.fillStyle = tinycolor(bgColor).setAlpha(alpha).toRgbString();
  ctx.strokeStyle = borderColor;
  ctx.lineWidth = lineWidth * resizeFactor;

  const bottomBorderY = height - markerPadding - triangleHeight;

  ctx.beginPath();
  // start top/left corner
  ctx.moveTo(markerPadding, radius + markerPadding);
  // top/left round corner
  ctx.arc(radius + markerPadding, radius + markerPadding, radius, Math.PI, 1.5 * Math.PI, false);
  // top border
  ctx.lineTo(width - radius - markerPadding, markerPadding);
  // top/right round corner
  ctx.arc(width - radius - markerPadding, radius + markerPadding, radius, 1.5 * Math.PI, 0, false);
  // right border
  ctx.lineTo(width - markerPadding, bottomBorderY - radius);
  // bottom/right round corner
  ctx.arc(width - radius - markerPadding, bottomBorderY - radius, radius, 0, 0.5 * Math.PI, false);

  // bottom border
  ctx.lineTo(triangleEndX, bottomBorderY);
  // triangle pointing down
  ctx.lineTo(width / 2, height - markerPadding);
  ctx.lineTo(triangleStartX, bottomBorderY);
  ctx.lineTo(markerPadding + radius, bottomBorderY);

  // bottom/left round corner
  ctx.arc(radius + markerPadding, bottomBorderY - radius, radius, 0.5 * Math.PI, Math.PI, false);
  // left border
  ctx.lineTo(markerPadding, radius + markerPadding);

  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.save();

  const pathPrepared = svgPath.replace('path://', '');
  const path = new Path2D(pathPrepared);
  const bbox = getPathBBox(pathPrepared);
  const scaledPath = new Path2D();
  const scaleFactor = Math.min(
    dimensions.iconContainerWidth / bbox.width,
    dimensions.iconContainerHeight / bbox.height,
  );
  const scaledWidth = bbox.width * scaleFactor;
  const scaledHeight = bbox.height * scaleFactor;
  const offsetX = (dimensions.iconContainerWidth - scaledWidth) / 2;
  const offsetY = (dimensions.iconContainerHeight - scaledHeight) / 2;
  const translateX = dimensions.iconContainerStartX + offsetX;
  const translateY = dimensions.iconContainerStartY + offsetY;

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  const matrix = svg.createSVGMatrix().scale(scaleFactor);

  scaledPath.addPath(path, matrix);

  ctx.translate(translateX, translateY);
  ctx.fillStyle = iconColor;
  ctx.fill(scaledPath);
  ctx.translate(-translateX, -translateY);

  // Draw debug lines
  // drawDebugLine([0, dimensions.textStartY], [width, dimensions.textStartY], ctx);
  // drawDebugLine([dimensions.textStartX, 0], [dimensions.textStartX, height], ctx);
  // drawDebugLine([0, dimensions.textEndY], [width, dimensions.textEndY], ctx);
  // drawDebugLine([dimensions.textEndX, 0], [dimensions.textEndX, height], ctx);
  // drawDebugLine(
  //   [0, dimensions.textEndY + dimensions.iconContainerHeight],
  //   [width, dimensions.textEndY + dimensions.iconContainerHeight],
  //   ctx,
  // );

  // Draw icon padding debug lines
  // drawDebugLine(
  //   [dimensions.iconContainerStartX, dimensions.iconContainerStartY],
  //   [dimensions.iconContainerStartX, dimensions.iconContainerEndY],
  //   ctx,
  // );
  // drawDebugLine(
  //   [dimensions.iconContainerEndX, dimensions.iconContainerStartY],
  //   [dimensions.iconContainerEndX, dimensions.iconContainerEndY],
  //   ctx,
  // );
  // drawDebugLine(
  //   [dimensions.iconContainerStartX, dimensions.iconContainerStartY],
  //   [dimensions.iconContainerEndX, dimensions.iconContainerStartY],
  //   ctx,
  // );
  // drawDebugLine(
  //   [dimensions.iconContainerStartX, dimensions.iconContainerEndY],
  //   [dimensions.iconContainerEndX, dimensions.iconContainerEndY],
  //   ctx,
  // );

  const image = await createImageBitmap(canvas);
  return { image, dimensions };
};

// @ts-expect-error - This is a debug function, not used in production code
const _drawDebugLine = (
  start: [number, number],
  end: [number, number],
  ctx: CanvasRenderingContext2D,
) => {
  const preStrokeStyle = ctx.strokeStyle;
  const preLineWidth = ctx.lineWidth;

  ctx.beginPath();
  ctx.moveTo(start[0], start[1]);
  ctx.lineTo(end[0], end[1]);
  ctx.lineWidth = 1;
  ctx.strokeStyle = RacemapColors.PaleBlue;
  ctx.setLineDash([2, 2]);
  ctx.stroke();
  ctx.setLineDash([]);

  ctx.strokeStyle = preStrokeStyle;
  ctx.lineWidth = preLineWidth;
};

function getPathBBox(svgPathData: string): DOMRect {
  // Create an offscreen SVG element
  const svgNS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(svgNS, 'svg');
  const path = document.createElementNS(svgNS, 'path');
  path.setAttribute('d', svgPathData);
  svg.appendChild(path);
  document.body.appendChild(svg); // Needed for getBBox to work in some browsers

  const bbox = path.getBBox();
  document.body.removeChild(svg);

  return bbox; // {x, y, width, height}
}
